import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Navigation } from 'lucide-react';

// Fix default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons for visits
const confirmedIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background:#4caf50;width:28px;height:28px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-size:14px;font-weight:bold;">✓</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -16],
});

const unconfirmedIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background:#ff9800;width:28px;height:28px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-size:14px;font-weight:bold;">?</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -16],
});

const startIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background:#1a237e;width:32px;height:32px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;color:white;font-size:16px;">▶</div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -18],
});

const endIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background:#f44336;width:32px;height:32px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;color:white;font-size:16px;">■</div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -18],
});

// Component to auto-fit map bounds
function FitBounds({ positions }) {
  const map = useMap();

  useEffect(() => {
    if (positions && positions.length > 0) {
      const bounds = L.latLngBounds(positions.map((p) => [p.lat || p[0], p.long || p.lng || p[1]]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [positions, map]);

  return null;
}

export default function SalesRepMap({ repData, selectedRep, loading, onSelectVisit, date }) {
  const defaultCenter = [20.5937, 78.9629]; // India center
  const defaultZoom = 5;

  if (!selectedRep) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-gray-100 text-gray-400">
        <MapPin size={64} strokeWidth={1} className="mb-4" />
        <p className="text-lg font-medium">Select a Sales Rep</p>
        <p className="text-sm mt-1">Choose a rep from the left panel to view their tracking path</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-900 mb-3"></div>
        <p className="text-gray-500 text-sm">Loading tracking data for {selectedRep.name}...</p>
      </div>
    );
  }

  const gpsPath = repData?.gpsPath || [];
  const visits = repData?.visits || [];
  const metrics = repData?.metrics || {};

  const hasGpsData = gpsPath.length > 0;
  const hasVisitLocations = visits.some((v) => v.latitude && v.longitude);

  // Build path coordinates
  const pathPositions = gpsPath.map((p) => [p.lat, p.long]);

  // All points for bounds fitting
  const allPoints = [
    ...gpsPath.map((p) => ({ lat: p.lat, long: p.long })),
    ...visits.filter((v) => v.latitude && v.longitude).map((v) => ({ lat: v.latitude, long: v.longitude })),
  ];

  if (!hasGpsData && !hasVisitLocations) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-gray-100 text-gray-400">
        <Navigation size={48} strokeWidth={1} className="mb-4" />
        <p className="text-lg font-medium">No tracking data</p>
        <p className="text-sm mt-1">No GPS logs or visit locations found for {selectedRep.name} on this date</p>
      </div>
    );
  }

  return (
    <div className="h-full relative">
      <MapContainer
        center={hasGpsData ? [gpsPath[0].lat, gpsPath[0].long] : defaultCenter}
        zoom={hasGpsData ? 13 : defaultZoom}
        className="h-full w-full"
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Auto-fit bounds */}
        {allPoints.length > 0 && <FitBounds positions={allPoints} />}

        {/* GPS Tracking Path Line */}
        {pathPositions.length > 1 && (
          <Polyline
            positions={pathPositions}
            pathOptions={{
              color: '#1a237e',
              weight: 3,
              opacity: 0.7,
              dashArray: null,
            }}
          />
        )}

        {/* Start Point */}
        {hasGpsData && (
          <Marker position={[gpsPath[0].lat, gpsPath[0].long]} icon={startIcon}>
            <Popup>
              <div className="text-sm">
                <p className="font-bold text-primary-900">Start Point</p>
                <p className="text-gray-600">
                  {new Date(gpsPath[0].timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </Popup>
          </Marker>
        )}

        {/* End Point */}
        {gpsPath.length > 1 && (
          <Marker
            position={[gpsPath[gpsPath.length - 1].lat, gpsPath[gpsPath.length - 1].long]}
            icon={endIcon}
          >
            <Popup>
              <div className="text-sm">
                <p className="font-bold text-red-600">End Point</p>
                <p className="text-gray-600">
                  {new Date(gpsPath[gpsPath.length - 1].timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Visit Markers (doctor/hospital pins) */}
        {visits
          .filter((v) => v.latitude && v.longitude)
          .map((visit, index) => (
            <Marker
              key={visit.id || index}
              position={[visit.latitude, visit.longitude]}
              icon={visit.confirmed ? confirmedIcon : unconfirmedIcon}
              eventHandlers={{
                click: () => onSelectVisit && onSelectVisit(visit),
              }}
            >
              <Popup>
                <div className="text-sm min-w-[180px]">
                  <p className="font-bold text-gray-800">
                    #{index + 1} {visit.doctor_name || 'Unknown Doctor'}
                  </p>
                  {visit.hospital_name && (
                    <p className="text-gray-500 text-xs">{visit.hospital_name}</p>
                  )}
                  <p className="text-gray-600 mt-1">
                    {new Date(visit.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                  <span
                    className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                      visit.confirmed
                        ? 'bg-green-100 text-green-700'
                        : 'bg-orange-100 text-orange-700'
                    }`}
                  >
                    {visit.confirmed ? 'Confirmed' : 'Unconfirmed'}
                  </span>
                  {visit.optional_notes && (
                    <p className="text-gray-400 text-xs mt-1 italic">{visit.optional_notes}</p>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
      </MapContainer>

      {/* Metrics Overlay */}
      <div className="absolute top-3 right-3 bg-white/95 backdrop-blur rounded-xl shadow-lg p-3 z-[1000] min-w-[200px]">
        <p className="text-xs font-semibold text-primary-900 uppercase tracking-wider mb-2">
          {selectedRep.name} — {date ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </p>
        <div className="space-y-1.5 text-xs">
          <div className="flex justify-between">
            <span className="text-gray-500">Visits</span>
            <span className="font-semibold">{metrics.totalVisits || 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Confirmed</span>
            <span className="font-semibold text-green-600">{metrics.confirmedVisits || 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Distance</span>
            <span className="font-semibold">{metrics.totalDistanceKm || 0} km</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Field Time</span>
            <span className="font-semibold">{metrics.fieldTimeHours || 0} hrs</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">GPS Points</span>
            <span className="font-semibold">{gpsPath.length}</span>
          </div>
          <div className="flex justify-between border-t pt-1.5 mt-1.5">
            <span className="text-gray-500">Bonus</span>
            <span className={`font-semibold ${metrics.bonusEligible ? 'text-green-600' : 'text-orange-500'}`}>
              {metrics.bonusEligible ? 'Eligible' : 'Not Yet'}
            </span>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur rounded-lg shadow-md p-2.5 z-[1000] text-xs">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-primary-900 inline-block"></span> Start
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-red-500 inline-block"></span> End
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span> Confirmed
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-orange-400 inline-block"></span> Unconfirmed
          </span>
          <span className="flex items-center gap-1">
            <span className="w-6 h-0.5 bg-primary-900 inline-block"></span> Path
          </span>
        </div>
      </div>
    </div>
  );
}
